import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  CheckboxControlValueAccessor,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorFieldComponent } from '../error-field/error-field.component';

/**
 * @example
 *  <siat-checkbox-field
 *   [formGroup]="formModificaStruttura"
 *   controlName="abilitato"
 *   label="Abilitato"
 *   id="abilitato"
 *   name="abilitatoName"
 *   labelPosition="after"
 *   >
 *  </siat-checkbox-field>
 */
@Component({
  selector: 'siat-checkbox-field',
  templateUrl: './checkbox-field.component.html',
  providers: [CheckboxControlValueAccessor],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    MatCheckboxModule,
    FormsModule,
  ],
})
export class CheckboxFieldComponent implements ControlValueAccessor {
  /**
   * The form group the field belongs to.
   */
  @Input()
  formGroup: FormGroup;

  /**
   * The name of the control within the form group.
   */
  @Input()
  controlName: string;

  /**
   * The field label.
   */
  @Input()
  label: string;

  /**
   * The field id.
   */
  @Input()
  id: string;

  /**
   * The field name.
   */
  @Input()
  name: string;

  /**
   * A flag telling whether the field belongs to a group or not.
   */
  @Input()
  belongsToGroup?: boolean;

  /**
   * The field id.
   */
  @Input()
  labelPosition: 'before' | 'after' = 'after';

  /**
   * The style class of the field.
   */
  @Input()
  fieldClass: string;

  /**
   * Gets the form control.
   */
  get control(): FormControl<boolean> {
    return this.formGroup.controls[this.controlName] as FormControl<boolean>;
  }

  /**
   * Gets the value of the control.
   */
  get controlValue(): boolean {
    return this.control.value;
  }

  /**
   * A flag telling whether the control is disabled or not.
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * @inheritdoc
   */
  private onChange: (value: boolean) => void = () => {};

  /**
   * @inheritdoc
   */
  onTouched: () => void = () => {};

  /**
   * The write value function.
   */
  writeValue(obj: boolean): void {
    this.control.setValue(obj);
  }

  /**
   * The register on change function.
   */
  registerOnChange(onChangeCallBack: () => void): void {
    this.onChange = onChangeCallBack;
    this.control.valueChanges.subscribe(this.onChange);
  }

  /**
   * The register on touched function.
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }
}
